// filtri/motore.ts — il motore dei filtri: dalla fotocamera al video che vede l'avversario.
//
//   fotocamera ─► MediaPipe (il MIO volto) ─► punti stabilizzati, posa, espressioni
//        │                                         │
//        └─► [shader del filtro] ─► canvas ◄─ [strato: disegna del filtro, con la bocca "bucata"] ─► captureStream
//
// - Il volto si analizza sul video GREZZO (mai su quello filtrato). Il risultato serve ai filtri e al rilevamento
//   della risata (SorgenteVolto, con l'ora del fotogramma): l'analizzatore lavora una volta sola.
// - Senza filtro il motore non disegna e analizza solo al ritmo del rilevamento; all'avversario va la fotocamera.
//   Con un filtro attivo analizza e disegna a ogni fotogramma nuovo della fotocamera.
// - La bocca resta visibile all'avversario anche se un filtro sbaglia: ciò che il filtro disegna sta su uno strato
//   separato in cui la zona della bocca viene svuotata (calcolata sui punti NON stabilizzati, che non ritardano).
// - Un filtro che va in errore viene spento (si torna alla fotocamera) e segnalato una volta. Se è il motore stesso
//   a sbagliare di continuo, si ferma del tutto e si gioca senza filtri.
import type { FaceLandmarker, RisultatoMediaPipe, SorgenteVolto } from "../gioco/rilevamento.ts";
import { blendshapeDa } from "../gioco/rilevamento.ts";
import type { Blendshape } from "../../../condiviso/risata.ts";
import { casualeConSeme, posaDa, zonaBocca, type Ellisse } from "./geometria.ts";
import { valoriParametri } from "./manifest.ts";
import { chiudiContestoGL, PassaggioGL } from "./shader.ts";
import { Stabilizzatore, type ParametriStabilizza } from "./stabilizza.ts";
import { dimensioniUscita, inPixel } from "./misure.ts";
import type { FiltroCaricato } from "./registro.ts";
import type { ContestoFiltro, Filtro, ManifestFiltro, Punto, ValoreParametro, Volto } from "./tipi.ts";
import { AFFANNO_DOPO_MS, QualitaAutomatica, type LivelloQualita } from "./qualita.ts";
import { conOmbra, disegnaConOmbra, fattoreProporzioni, voltoPerFiltro } from "./proporzioni.ts";
import { PassaggioPelle } from "./pelle.ts";
import { componiDisegno, componiPelle } from "./compositore.ts";

/** Tempo massimo per preparare un filtro (caricare le immagini): fa parte del contratto dei filtri. */
const PREPARA_MAX_MS = 2_000;
/** dtMs passato ai filtri: mai più di così (dopo una pausa le animazioni non "saltano"). */
const DT_MASSIMO_MS = 100;

export interface OpzioniMotore {
  flusso: MediaStream;
  analizzatore: FaceLandmarker;
  /** Ora del server (per datare le espressioni come il resto del rilevamento). */
  orario: () => number;
  /** Fotogrammi al secondo con un filtro attivo (disegno e analisi). */
  fps: number;
  /** Analisi al secondo senza filtro (= ritmo del rilevamento della risata per il proprio volto). */
  analisiSenzaFiltro: number;
  latoMassimo: number;
  /** 0..1: quanto riportare la bocca all'immagine vera nei filtri shader (lo strato del disegno si svuota del tutto). */
  ripristinoBocca: number;
  margineBocca: number;
  tieniVoltoMs: number;
  stabilizza: ParametriStabilizza;
  /** Errori di fila del motore oltre i quali si ferma tutto. */
  erroriMassimi: number;
  /** Un filtro si è fermato per un errore (chiamato una volta per filtro). */
  suErrore: (errore: unknown, idFiltro: string) => void;
  /** Il video con il filtro è pronto (true) o non va più usato (false): si invia la fotocamera. */
  suUscita: (attiva: boolean) => void;
  /** Il motore si è fermato da solo per troppi errori. */
  suGuasto: (errore: unknown) => void;
  /** v034: qualità automatica (telefoni lenti): se manca, sempre al massimo. */
  qualita?: {
    attiva: boolean;
    iniziale: number;
    suCambio?: (livello: number, l: LivelloQualita) => void;
    /**
     * v046: anche al livello più basso il telefono non ce la fa da AFFANNO_DOPO_MS con questo filtro (una volta per
     * filtro scelto): chi usa il motore può passare a un filtro più leggero (potenza.ts).
     */
    suAffanno?: (idFiltro: string) => void;
  };
}

interface FiltroAttivo {
  id: string;
  istanza: Filtro;
  manifest: ManifestFiltro;
  parametri: Record<string, ValoreParametro>;
  gl: PassaggioGL | null;
  /** v043: la pelle sul viso (se il filtro ce l'ha) */
  pelle: PassaggioPelle | null;
  inizioMs: number;
  /** v034: correzione delle proporzioni per questo filtro */
  fattore: number;
  /** v034: ombra morbida sotto gli oggetti */
  ombra: boolean;
}

type VideoConCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (f: (ora: number) => void) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

export class MotoreFiltri implements SorgenteVolto {
  readonly uscita: MediaStream;
  private o: OpzioniMotore;
  private video: VideoConCallback;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private strato: HTMLCanvasElement;
  private sctx: CanvasRenderingContext2D;
  private canvasGL: HTMLCanvasElement | null = null;
  private canvasPelleGL: HTMLCanvasElement | null = null;
  private stratoPelle: HTMLCanvasElement;
  private pctx: CanvasRenderingContext2D;
  private filtro: FiltroAttivo | null = null;
  private stab: Stabilizzatore;
  private volto: Volto | null = null;
  private zonaGrezza: Ellisse | null = null;
  private voltoVistoMs = -Infinity;
  private risultato: { versione: number; espressioni: Blendshape | null; t: number } = { versione: 0, espressioni: null, t: 0 };
  private ultimoTempoMediaPipe = 0;
  private ultimoFotogrammaMs = 0;
  private prossimoMs = 0;
  private fermo = false;
  private rAF = 0;
  private rVFC = 0;
  private casuale = casualeConSeme(1);
  private riduci: MediaQueryList | null;
  private sceltaInCorso = 0;
  private uscitaAttiva = false;
  private erroriDiFila = 0;
  private segnalati = new Set<string>();
  private sblocca: (() => void) | null = null;
  /** v034: qualità automatica e contatore per «un'analisi ogni N fotogrammi» */
  readonly qualita: QualitaAutomatica;
  private contaAnalisi = 0;
  /** v046: `suAffanno` già chiamato per il filtro attivo */
  private affannoDetto = false;

  constructor(o: OpzioniMotore) {
    this.o = o;
    // Prima canvas e traccia (possono mancare su telefoni vecchi): se manca qualcosa non resta nulla in giro.
    this.canvas = document.createElement("canvas");
    this.canvas.width = 2;
    this.canvas.height = 2;
    const ctx = this.canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!ctx) throw new Error("canvas 2D non disponibile");
    this.ctx = ctx;
    if (typeof this.canvas.captureStream !== "function") throw new Error("captureStream non disponibile");
    const traccia = this.canvas.captureStream(o.fps).getVideoTracks()[0];
    if (!traccia) throw new Error("captureStream senza traccia video");
    this.strato = document.createElement("canvas");
    const sctx = this.strato.getContext("2d");
    if (!sctx) {
      traccia.stop();
      throw new Error("canvas 2D non disponibile");
    }
    this.sctx = sctx;
    this.stratoPelle = document.createElement("canvas");
    const pctx = this.stratoPelle.getContext("2d");
    if (!pctx) {
      traccia.stop();
      throw new Error("canvas 2D non disponibile");
    }
    this.pctx = pctx;
    this.uscita = new MediaStream([traccia, ...o.flusso.getAudioTracks()]);
    this.stab = new Stabilizzatore(o.stabilizza);
    this.qualita = new QualitaAutomatica({ attiva: o.qualita?.attiva ?? false, iniziale: o.qualita?.iniziale ?? 0, massimi: { fps: o.fps, lato: o.latoMassimo } });
    this.riduci = window.matchMedia?.("(prefers-reduced-motion: reduce)") ?? null;

    // Il video grezzo resta nel documento (invisibile): su iPhone un video staccato può smettere di dare fotogrammi.
    this.video = document.createElement("video");
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute("playsinline", "");
    this.video.setAttribute("aria-hidden", "true");
    Object.assign(this.video.style, { position: "fixed", left: "0", top: "0", width: "2px", height: "2px", opacity: "0", pointerEvents: "none" });
    this.video.srcObject = o.flusso;
    document.body.appendChild(this.video);
    this.avviaVideo();
    this.pianifica();
  }

  /** play() può essere rifiutato (risparmio energetico, regole di autoplay): si riprova al primo tocco. */
  private avviaVideo(): void {
    void this.video.play().catch(() => {
      if (this.sblocca || this.fermo) return;
      this.sblocca = () => {
        document.removeEventListener("pointerdown", this.sblocca!);
        this.sblocca = null;
        if (!this.fermo) this.avviaVideo();
      };
      document.addEventListener("pointerdown", this.sblocca);
    });
  }

  /** SorgenteVolto: l'ultimo risultato dell'analisi del mio volto, con l'ora del server del fotogramma. */
  ultimo(): { versione: number; espressioni: Blendshape | null; t: number } {
    return this.risultato;
  }

  get idFiltro(): string | null {
    return this.filtro?.id ?? null;
  }

  /**
   * Cambia filtro (null = nessuno). Il vecchio resta attivo finché il nuovo non è pronto; se il nuovo non si
   * prepara in tempo o va in errore si resta senza filtro, lo si segnala e la promessa viene rifiutata.
   */
  async scegli(f: FiltroCaricato | null, parametriScelti: Readonly<Record<string, unknown>> | null): Promise<void> {
    const mia = ++this.sceltaInCorso;
    if (!f) {
      this.sostituisci(null);
      return;
    }
    let istanza: Filtro | null = null;
    let gl: PassaggioGL | null = null;
    let pelle: PassaggioPelle | null = null;
    let parametri: Record<string, ValoreParametro>;
    try {
      parametri = valoriParametri(f.manifest, parametriScelti);
      istanza = f.crea();
      const prepara = istanza.prepara?.(f.risorse);
      if (prepara) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        await Promise.race([
          prepara,
          new Promise((_, no) => (timer = setTimeout(() => no(new Error("prepara: tempo scaduto")), PREPARA_MAX_MS))),
        ]).finally(() => clearTimeout(timer));
      }
      if (istanza.shader) gl = new PassaggioGL(this.canvasShader(), istanza.shader, this.o.ripristinoBocca);
      if (istanza.pelle) pelle = new PassaggioPelle(this.canvasPelle(), istanza.pelle);
    } catch (e) {
      this.liberaFiltro(istanza, gl, pelle);
      if (mia === this.sceltaInCorso) this.sostituisci(null);
      this.segnala(e, f.manifest.id);
      throw e;
    }
    if (mia !== this.sceltaInCorso || this.fermo) {
      // Nel frattempo è stato scelto un altro filtro (o il motore è stato fermato).
      this.liberaFiltro(istanza, gl, pelle);
      return;
    }
    this.sostituisci({ id: f.manifest.id, istanza, manifest: f.manifest, parametri, gl, pelle, inizioMs: performance.now(), fattore: fattoreProporzioni(f.manifest), ombra: conOmbra(f.manifest) });
  }

  /** Un solo canvas WebGL per tutta la partita (iPhone ha un tetto alla memoria dei canvas); nuovo se perso. */
  private canvasShader(): HTMLCanvasElement {
    const gl = this.canvasGL?.getContext("webgl2") as WebGL2RenderingContext | null | undefined;
    if (!this.canvasGL || !gl || gl.isContextLost()) {
      if (this.canvasGL) chiudiContestoGL(this.canvasGL);
      this.canvasGL = document.createElement("canvas");
      this.canvasGL.addEventListener("webglcontextlost", (e) => e.preventDefault());
    }
    return this.canvasGL;
  }

  /** v043: anche la pelle ha un solo canvas WebGL per tutta la partita. */
  private canvasPelle(): HTMLCanvasElement {
    const gl = this.canvasPelleGL?.getContext("webgl2") as WebGL2RenderingContext | null | undefined;
    if (!this.canvasPelleGL || !gl || gl.isContextLost()) {
      if (this.canvasPelleGL) chiudiContestoGL(this.canvasPelleGL);
      this.canvasPelleGL = document.createElement("canvas");
      this.canvasPelleGL.addEventListener("webglcontextlost", (e) => e.preventDefault());
    }
    return this.canvasPelleGL;
  }

  private liberaFiltro(istanza: Filtro | null, gl: PassaggioGL | null, pelle: PassaggioPelle | null = null): void {
    try {
      istanza?.libera?.();
    } catch {
      // un filtro che sbaglia anche nel liberarsi non deve fermare il motore
    }
    try {
      gl?.libera();
    } catch {
      // contesto già perso: niente da liberare
    }
    try {
      pelle?.libera();
    } catch {
      // contesto già perso
    }
  }

  private sostituisci(nuovo: FiltroAttivo | null): void {
    const vecchio = this.filtro;
    this.filtro = nuovo;
    this.casuale = casualeConSeme((nuovo?.manifest.numero ?? 0) + 1);
    // v046: un filtro nuovo costa diverso: la qualità ricomincia a misurare (il livello resta)
    this.qualita.ricomincia(performance.now());
    this.affannoDetto = false;
    if (vecchio) this.liberaFiltro(vecchio.istanza, vecchio.gl, vecchio.pelle);
    // Senza filtro all'avversario va subito la fotocamera; con un filtro, solo dal primo fotogramma disegnato.
    if (!nuovo) this.impostaUscita(false);
  }

  private impostaUscita(attiva: boolean): void {
    if (this.uscitaAttiva === attiva) return;
    this.uscitaAttiva = attiva;
    this.o.suUscita(attiva);
  }

  private segnala(e: unknown, id: string): void {
    if (this.segnalati.has(id)) return;
    this.segnalati.add(id);
    this.o.suErrore(e, id);
  }

  ferma(): void {
    if (this.fermo) return;
    this.fermo = true;
    cancelAnimationFrame(this.rAF);
    this.video.cancelVideoFrameCallback?.(this.rVFC);
    if (this.sblocca) document.removeEventListener("pointerdown", this.sblocca);
    this.sostituisci(null);
    for (const t of this.uscita.getVideoTracks()) t.stop(); // solo la traccia del canvas: l'audio è della fotocamera
    this.video.srcObject = null;
    this.video.remove();
    if (this.canvasGL) chiudiContestoGL(this.canvasGL);
    if (this.canvasPelleGL) chiudiContestoGL(this.canvasPelleGL);
    for (const c of [this.canvas, this.strato, this.stratoPelle]) {
      c.width = 0;
      c.height = 0;
    }
  }

  /**
   * Un fotogramma per ogni fotogramma NUOVO della fotocamera (requestVideoFrameCallback, dove c'è); altrimenti
   * a ritmo regolare con l'accumulatore, senza analizzare due volte lo stesso fotogramma.
   */
  private pianifica(): void {
    if (this.fermo) return;
    if (this.video.requestVideoFrameCallback) {
      this.rVFC = this.video.requestVideoFrameCallback((ora) => {
        this.passo(ora);
        this.pianifica();
      });
    } else {
      this.rAF = requestAnimationFrame((ora) => {
        this.passo(ora);
        this.pianifica();
      });
    }
  }

  private passo(ora: number): void {
    const intervallo = 1000 / (this.filtro ? this.qualita.attuale.fps : this.o.analisiSenzaFiltro);
    if (ora + 1 < this.prossimoMs) return;
    // Accumulatore: ritmo medio giusto anche quando lo schermo va a 60 o 120 Hz.
    this.prossimoMs = Math.max(this.prossimoMs + intervallo, ora - intervallo);
    try {
      const t0 = performance.now();
      const disegnato = this.fotogramma(ora);
      // v034: quanto è costato davvero (solo con un filtro: senza, il motore non disegna)
      if (disegnato) {
        const n = this.qualita.misura(performance.now() - t0, ora);
        if (n !== null) this.o.qualita?.suCambio?.(n, this.qualita.attuale);
        if (!this.affannoDetto && this.filtro && this.qualita.affannoMs(ora) >= AFFANNO_DOPO_MS) {
          this.affannoDetto = true;
          this.o.qualita?.suAffanno?.(this.filtro.id);
        }
      }
      this.erroriDiFila = 0;
    } catch (e) {
      // Errore del motore (non del filtro): si spegne il filtro; se continua, si ferma tutto.
      if (this.filtro) {
        const id = this.filtro.id;
        this.sostituisci(null);
        this.segnala(e, id);
      }
      if (++this.erroriDiFila >= this.o.erroriMassimi) {
        this.ferma();
        this.o.suGuasto(e);
      }
    }
  }

  private analizza(ora: number, w: number, h: number): void {
    const t = Math.max(ora, this.ultimoTempoMediaPipe + 1); // MediaPipe vuole tempi crescenti
    let r: RisultatoMediaPipe;
    try {
      r = this.o.analizzatore.detectForVideo(this.video, t);
    } catch {
      // Analisi non riuscita (es. GPU persa): NON vuol dire "volto assente", si salta il fotogramma.
      return;
    }
    this.ultimoTempoMediaPipe = t;
    const espressioni = blendshapeDa(r);
    this.risultato = { versione: this.risultato.versione + 1, espressioni, t: this.o.orario() };
    if (!this.filtro) return; // senza filtro servono solo le espressioni
    const lm = r.faceLandmarks?.[0];
    if (lm && lm.length > 0) {
      const grezzi = inPixel(lm, w, h);
      const pGrezzi: Punto[] = new Array(lm.length);
      for (let i = 0; i < lm.length; i++) pGrezzi[i] = { x: grezzi[i * 3]!, y: grezzi[i * 3 + 1]!, z: grezzi[i * 3 + 2]! };
      // Zona della bocca sui punti grezzi: segue subito anche i movimenti veloci.
      this.zonaGrezza = zonaBocca({ punti: pGrezzi, espressioni: {}, posa: posaDa(pGrezzi), matrice: null }, this.o.margineBocca);
      const fermi = this.stab.applica(grezzi, t, this.volto?.posa.scala ?? w * 0.15);
      const punti: Punto[] = new Array(lm.length);
      for (let i = 0; i < lm.length; i++) punti[i] = { x: fermi[i * 3]!, y: fermi[i * 3 + 1]!, z: fermi[i * 3 + 2]! };
      const m = r.facialTransformationMatrixes?.[0]?.data;
      this.volto = { punti, espressioni: espressioni ?? {}, posa: posaDa(punti), matrice: m ? Float32Array.from(m) : null };
      this.voltoVistoMs = t;
    } else if (t - this.voltoVistoMs > this.o.tieniVoltoMs) {
      this.volto = null;
      this.zonaGrezza = null;
      this.stab.azzera();
    }
  }

  /** Un fotogramma; true se ha disegnato con un filtro (per la qualità automatica). */
  private fotogramma(ora: number): boolean {
    const v = this.video;
    if (v.readyState < 2 || v.videoWidth === 0) return false;
    const q = this.qualita.attuale;
    const { w, h } = dimensioniUscita(v.videoWidth, v.videoHeight, this.filtro ? q.lato : this.o.latoMassimo);
    // v034: con la qualità ridotta il volto si analizza un fotogramma sì e uno no (in mezzo vale l'ultimo); mai
    // quando le misure del canvas cambiano (i punti sarebbero nella scala vecchia)
    const cambiaMisure = this.canvas.width !== w || this.canvas.height !== h;
    if (!this.filtro || q.analisiOgni <= 1 || cambiaMisure || this.contaAnalisi++ % q.analisiOgni === 0) this.analizza(ora, w, h);
    const f = this.filtro;
    if (!f) {
      this.ultimoFotogrammaMs = 0;
      return false;
    }
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.strato.width = w;
      this.strato.height = h;
      this.stratoPelle.width = w;
      this.stratoPelle.height = h;
      this.stab.azzera();
    }
    const dt = this.ultimoFotogrammaMs ? Math.min(DT_MASSIMO_MS, ora - this.ultimoFotogrammaMs) : 0;
    this.ultimoFotogrammaMs = ora;
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    const c: ContestoFiltro = {
      larghezza: w,
      altezza: h,
      tempoMs: ora - f.inizioMs,
      dtMs: dt,
      // v034: scala corretta per le proporzioni dei volti veri (proporzioni.ts)
      volto: voltoPerFiltro(this.volto, f.fattore),
      parametri: f.parametri,
      riduciAnimazioni: this.riduci?.matches === true || q.leggero,
      casuale: this.casuale,
    };
    try {
      const zona = this.volto ? this.zonaGrezza : null;
      if (f.gl) {
        f.gl.esegui(v, c, zona);
        ctx.drawImage(f.gl.canvas, 0, 0, w, h);
      } else ctx.drawImage(v, 0, 0, w, h);
      // v043: la pelle sul viso (sotto il disegno, senza ombra); la luce la prende dal fotogramma appena fatto
      if (f.pelle && componiPelle(this.pctx, f.pelle, f.istanza, c, f.gl ? f.gl.canvas : v, this.volto, zona)) ctx.drawImage(this.stratoPelle, 0, 0);
      if (componiDisegno(this.sctx, f.istanza, c, zona)) {
        if (q.ombra && f.ombra && this.volto) disegnaConOmbra(ctx, this.strato, this.volto.posa.scala);
        else ctx.drawImage(this.strato, 0, 0);
      }
    } catch (e) {
      this.sostituisci(null);
      this.segnala(e, f.id);
      return false;
    }
    this.impostaUscita(true);
    return true;
  }
}
